import React, { useEffect, useState } from "react"
import { Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { POST } from 'helpers/api'
import {
	isEimzoAvailable, installApiKeys, listCertificates, loadKey, createPkcs7,
	certTin, certName, certOrg, certType, certSerial, certValidFrom, certValidTo,
} from 'helpers/eimzo'

// E-IMZO dates come as "YYYY.MM.DD HH:mm:ss" (or similar) → show DD.MM.YYYY.
function fmtDate(v) {
	if (!v) return ''
	const s = String(v).trim().slice(0, 10)
	const p = s.split(/[.\-/]/)
	if (p.length === 3) return p[0].length === 4 ? `${p[2]}.${p[1]}.${p[0]}` : `${p[0]}.${p[1]}.${p[2]}`
	return s
}

// Didox login: obtain a company session token (user-key) via E-IMZO key or password.
function DidoxLoginModal({ show, onClose, onSuccess, defaultTin = '' }) {
	const { t } = useTranslation()

	const [tab, setTab] = useState('eimzo')
	const [loading, setLoading] = useState(false)

	// E-IMZO
	const [certs, setCerts] = useState([])
	const [certsLoading, setCertsLoading] = useState(false)
	const [selectedIdx, setSelectedIdx] = useState(null)
	const [eimzoError, setEimzoError] = useState('')

	// password
	const [username, setUsername] = useState(defaultTin)
	const [password, setPassword] = useState('')

	async function loadCerts() {
		setEimzoError('')
		// SDK library not loaded at all (e-imzo.js / e-imzo-client.js missing) → distinct message.
		if (!isEimzoAvailable()) {
			setEimzoError(t('EIMZO_SDK_MISSING'))
			return
		}
		setCertsLoading(true)
		try {
			await installApiKeys()
			const list = await listCertificates()
			setCerts(list)
			if (list.length === 0) setEimzoError(t('EIMZO_NO_KEYS'))
		} catch (e) {
			// SDK is loaded but install-keys / connection / cert-list failed — surface the reason.
			setEimzoError(t('EIMZO_NOT_AVAILABLE') + (e && e.message ? ` (${e.message})` : ''))
		} finally {
			setCertsLoading(false)
		}
	}

	useEffect(() => {
		if (show && tab === 'eimzo') loadCerts()
	}, [show, tab]) // eslint-disable-line react-hooks/exhaustive-deps

	function handleToken(res) {
		const body = res?.data
		if (body?.success && body.token) {
			onSuccess(body.token)
			toast.success(t('SUCCESS'))
			onClose()
			return true
		}
		toast.error(body?.message || t('DIDOX_AUTH_FAILED'))
		return false
	}

	async function loginByKey() {
		if (selectedIdx === null) {
			toast.error(t('EIMZO_SELECT_KEY'))
			return
		}
		const vo = certs[selectedIdx]
		const tin = certTin(vo)
		if (!tin) {
			toast.error(t('EIMZO_NO_TIN'))
			return
		}
		setLoading(true)
		try {
			const keyId = await loadKey(vo)
			const { pkcs7, signatureHex } = await createPkcs7(keyId, tin)
			const res = await POST('/services/web/api/didox/login?by=key', { tin, pkcs7, signatureHex }, { loader: true })
			handleToken(res)
		} catch (e) {
			toast.error(t('EIMZO_SIGN_FAILED'))
		} finally {
			setLoading(false)
		}
	}

	async function loginByPassword(e) {
		e.preventDefault()
		if (!username || !password) {
			toast.error(t('REQUIRED_FIELD'))
			return
		}
		setLoading(true)
		try {
			const res = await POST('/services/web/api/didox/login?by=password', { username, password }, { loader: true })
			handleToken(res)
		} finally {
			setLoading(false)
		}
	}

	return (
		<Modal show={show} onHide={onClose} centered>
			<Modal.Header closeButton>
				<Modal.Title>{t('DIDOX_LOGIN')}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<ul className="nav nav-tabs mb-3">
					<li className="nav-item">
						<button className={`nav-link ${tab === 'eimzo' ? 'active' : ''}`} onClick={() => setTab('eimzo')}>E-IMZO</button>
					</li>
					<li className="nav-item">
						<button className={`nav-link ${tab === 'password' ? 'active' : ''}`} onClick={() => setTab('password')}>{t('PASSWORD')}</button>
					</li>
				</ul>

				{tab === 'eimzo' &&
					<div>
						{eimzoError &&
							<div className="alert alert-warning d-flex justify-content-between align-items-center">
								<span>{eimzoError}</span>
								<button className="btn btn-sm btn-outline-dark" onClick={loadCerts}>{t('REFRESH')}</button>
							</div>
						}
						{certsLoading && <div className="text-center py-3"><span className="spinner-border spinner-border-sm me-2"></span>{t('LOADING')}</div>}
						{!certsLoading && certs.length > 0 &&
							<div className="mb-3" style={{ maxHeight: 320, overflowY: 'auto' }}>
								{certs.map((vo, idx) => {
									const selected = selectedIdx === idx
									const org = certOrg(vo)
									return (
										<button key={idx} type="button"
											onClick={() => setSelectedIdx(idx)}
											className="w-100 text-start mb-2"
											style={{
												border: '1px solid #e5e9f2',
												borderLeft: `4px solid ${selected ? '#5b73e8' : 'transparent'}`,
												background: selected ? '#f5f7ff' : '#fff',
												borderRadius: 8,
												padding: '10px 14px',
												color: '#0f1b2d', // explicit: otherwise name/INN inherit the button's invisible default color
											}}>
											<div className="d-flex justify-content-between align-items-start">
												<span className="fw-bold text-uppercase" style={{ fontSize: 13 }}>{certName(vo) || '—'}</span>
												<span className="badge bg-light text-dark ms-2" style={{ fontWeight: 500 }}>{certType(vo)}</span>
											</div>
											<div className="small mt-1"><span className="text-muted">{t('TIN')}:</span> <b>{certTin(vo) || '—'}</b></div>
											{org &&
												<div className="small"><span className="text-muted">{t('EIMZO_ORG')}:</span> {org}</div>
											}
											<div className="small text-muted mt-1">
												№ {certSerial(vo) || '—'}
												{(certValidFrom(vo) || certValidTo(vo)) ? ` · ${fmtDate(certValidFrom(vo))} – ${fmtDate(certValidTo(vo))}` : ''}
											</div>
										</button>
									)
								})}
							</div>
						}
						<button className="btn btn-primary w-100" disabled={loading || selectedIdx === null} onClick={loginByKey}>
							{loading ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="uil uil-key-skeleton me-2"></i>}
							{t('LOGIN')}
						</button>
					</div>
				}

				{tab === 'password' &&
					<form onSubmit={loginByPassword}>
						<div className="form-group mb-2">
							<label>{t('TIN')} / PINFL</label>
							<input type="text" className="form-control" value={username} onChange={(e) => setUsername(e.target.value)} />
						</div>
						<div className="form-group mb-3">
							<label>{t('PASSWORD')}</label>
							<input type="password" className="form-control" value={password} onChange={(e) => setPassword(e.target.value)} />
						</div>
						<button type="submit" className="btn btn-primary w-100" disabled={loading}>
							{loading ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="uil uil-signin me-2"></i>}
							{t('LOGIN')}
						</button>
					</form>
				}
			</Modal.Body>
		</Modal>
	)
}

export default DidoxLoginModal
